/**
 * What a job was sold, what it has cost so far, and what is left of it.
 *
 * <p>Both dashboards used to answer only one of those at a time. The
 * technician's Today card showed `gesch. 1h 15m` until the first timer
 * ran and `deine Zeit 52m` after it, never both — so the one subtraction
 * that matters, "am I about to run over", was left to the reader. The
 * manager's side showed a total tracked figure with no estimate beside
 * it at all. Same three numbers, two screens, neither doing the
 * arithmetic.
 *
 * <p><b>Remaining is the loud one.</b> Agreed and worked are the inputs
 * and sit small above it; the number somebody acts on is what is LEFT,
 * and it is the only thing here at display weight.
 *
 * <p><b>Remaining counts everyone's time, not the reader's.</b> The
 * estimate was given for the whole job, so measuring it against one
 * technician's own clock reads 40m left on a job two people have already
 * run over. `workedMinutes` is the job's total; a technician's personal
 * figure belongs next to it as {@link TimeBudgetProps.ownMinutes}, which
 * is a different question ("how much of this was mine") and is never
 * what the subtraction uses.
 *
 * <p>Presentational and stateless. It does not tick: a live figure means
 * the caller is already re-rendering on a clock (`useNow`) and folding
 * the running span into `workedMinutes` — the same `taskMinutes` /
 * `labourMinutesAt` the rest of each app computes elapsed time with.
 * Giving this component its own timer would put a second clock on screen
 * that disagreed with the first by up to a minute.
 *
 * <p>Styling sticks to tokens BOTH apps map — `error`, `warning`,
 * `accent`, and the `glass-*` family, all of which resolve from the
 * shared tokens.css. `critical` is deliberately not used: it is defined
 * in management's own brand.css and would render as nothing on the
 * technician side. The state glyphs are inline SVG for the same reason —
 * management draws with lucide, the technician app with phosphor, and a
 * shared component that imported either would be importing an icon set
 * one of its two hosts does not have.
 */
import { formatDuration } from '../../utils'

/** Under this many minutes left, the figure goes red. Asked for by the shop. */
export const URGENT_MINUTES = 10

/**
 * Amber before red, as a FRACTION of what the job was sold for.
 *
 * <p>A fixed warning threshold cannot serve both ends of the catalogue:
 * ten minutes out of a four-hour job is not worth a colour, and on a
 * twenty-minute job it is the whole warning. A quarter left is the same
 * amount of "nearly there" whatever the job is worth.
 *
 * <p>It can land inside {@link URGENT_MINUTES} on a short job — a 20m
 * job's quarter is 5m, already red. Red wins there, which is correct:
 * the ladder only ever skips a rung, it never goes backwards.
 */
const WARN_FRACTION = 0.25

export type TimeBudgetState = 'running' | 'paused' | 'idle'

export interface TimeBudgetLabels {
  /** Names the estimate, e.g. "gesch." / "est." */
  readonly agreed: string
  /** Names the time spent, e.g. "gearbeitet" / "worked" */
  readonly worked: string
  /** Trails the remaining figure, e.g. "übrig" / "left" */
  readonly left: string
  /** Trails it once the job is past its estimate, e.g. "über" / "over" */
  readonly over: string
  /** Names {@link TimeBudgetProps.ownMinutes}, e.g. "deine" / "yours" */
  readonly own?: string
  /** Accessible names for the state glyph. */
  readonly running: string
  readonly paused: string
  readonly idle: string
}

export interface TimeBudgetProps {
  /**
   * What the customer agreed to, minutes. ZERO means the job was never
   * sized — the catalogue stopped carrying durations, so this is an
   * ordinary state, not missing data. There is no budget to spend and
   * the component says so by showing the spend alone.
   */
  readonly agreedMinutes: number
  /** Time booked against the job by EVERYONE, minutes. Live if the caller ticks. */
  readonly workedMinutes: number
  /** The reader's own share of {@link workedMinutes}. Omitted on manager surfaces. */
  readonly ownMinutes?: number
  /** Whether a timer is running on this job right now. */
  readonly state: TimeBudgetState
  readonly labels: TimeBudgetLabels
  /** `sm` for list rows and cards, `md` for a detail header. */
  readonly size?: 'sm' | 'md'
  readonly className?: string
}

type Tone = 'over' | 'urgent' | 'warn' | 'calm'

/** The ladder, top rung first — see {@link WARN_FRACTION} on the overlap. */
function toneFor(remaining: number, agreed: number): Tone {
  if (remaining < 0) return 'over'
  if (remaining <= URGENT_MINUTES) return 'urgent'
  if (remaining <= agreed * WARN_FRACTION) return 'warn'
  return 'calm'
}

const TONE_TEXT: Readonly<Record<Tone, string>> = {
  over: 'text-error',
  urgent: 'text-error',
  warn: 'text-warning',
  calm: 'text-glass-text',
}

const TONE_BAR: Readonly<Record<Tone, string>> = {
  over: 'bg-error',
  urgent: 'bg-error',
  warn: 'bg-warning',
  calm: 'bg-accent',
}

/**
 * Running, paused, or nobody on it.
 *
 * <p>A filled shape for each of the first two, because the pair reads as
 * a pair; an open ring for idle, which is the absence of both rather
 * than a third activity. Green only while it actually runs — a paused
 * job wearing the running colour is the one mistake this glyph exists to
 * prevent.
 */
function StateGlyph({
  state,
  label,
  px,
}: {
  readonly state: TimeBudgetState
  readonly label: string
  readonly px: number
}) {
  const tint =
    state === 'running' ? 'text-confirm' : state === 'paused' ? 'text-warning' : 'text-glass-text-muted'
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      role="img"
      aria-label={label}
      className={`shrink-0 ${tint}`}
    >
      {state === 'running' && <path d="M5 3.4v9.2l7.5-4.6z" fill="currentColor" />}
      {state === 'paused' && (
        <>
          <rect x="4" y="3.4" width="2.8" height="9.2" rx="0.9" fill="currentColor" />
          <rect x="9.2" y="3.4" width="2.8" height="9.2" rx="0.9" fill="currentColor" />
        </>
      )}
      {state === 'idle' && (
        <circle cx="8" cy="8" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      )}
    </svg>
  )
}

export function TimeBudget({
  agreedMinutes,
  workedMinutes,
  ownMinutes,
  state,
  labels,
  size = 'sm',
  className,
}: TimeBudgetProps) {
  const agreed = Math.max(0, Math.round(agreedMinutes))
  const worked = Math.max(0, Math.round(workedMinutes))
  const sized = agreed > 0
  const remaining = agreed - worked
  const tone = toneFor(remaining, agreed)

  const glyphPx = size === 'md' ? 13 : 11
  const metaClass = size === 'md' ? 'text-[11.5px]' : 'text-[10.5px]'
  const figureClass = size === 'md' ? 'text-[19px]' : 'text-[15px]'

  const stateLabel =
    state === 'running' ? labels.running : state === 'paused' ? labels.paused : labels.idle

  // Full and red the moment the job is over, rather than wrapping or
  // stopping short: past the estimate the bar is no longer measuring
  // progress towards anything, it is saying the budget is gone.
  const filledPct = sized ? Math.min(100, Math.round((worked / agreed) * 100)) : 0

  return (
    <div className={`flex min-w-0 flex-col gap-0.5 ${className ?? ''}`}>
      <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${metaClass} text-glass-text-muted`}>
        <StateGlyph state={state} label={stateLabel} px={glyphPx} />
        {sized && (
          <span className="tabular-nums">
            {labels.agreed} {formatDuration(agreed, { days: true })}
          </span>
        )}
        {sized && <span aria-hidden="true">·</span>}
        <span className="tabular-nums">
          {labels.worked} {formatDuration(worked, { days: true })}
        </span>
        {/* Whose time, when the reader has a stake of their own. Never the
            basis of the subtraction — see the note at the top. */}
        {ownMinutes != null && labels.own && ownMinutes > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">
              {labels.own} {formatDuration(ownMinutes, { days: true })}
            </span>
          </>
        )}
      </div>

      {sized && (
        <>
          <div className={`font-bold tabular-nums leading-tight ${figureClass} ${TONE_TEXT[tone]}`}>
            {remaining < 0
              ? `${formatDuration(-remaining, { days: true })} ${labels.over}`
              : `${formatDuration(remaining, { days: true })} ${labels.left}`}
          </div>
          <div
            className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-glass-fill-strong"
            aria-hidden="true"
          >
            <div
              className={`h-full rounded-full transition-[width] ${TONE_BAR[tone]}`}
              style={{ width: `${filledPct}%` }}
            />
          </div>
        </>
      )}
    </div>
  )
}
